import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterSelect from "@/components/base/FilterSelect";
import EmptyState from "@/components/base/EmptyState";
import WorkCard from "@/components/feature/WorkCard";
import { workItems, sortByDate, sortByPopularity } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEP = 8;

export default function WorksPage() {
  usePageMeta({
    title: "作品推荐 - 星空动漫",
    description:
      "星空动漫作品推荐频道精选动画与漫画，支持按类型、地区、年份筛选，并按热度、更新日期、评分排序，帮你快速找到想看的作品。",
    keywords: "作品推荐,动画推荐,漫画推荐,新番列表,动漫评分,星空动漫",
  });

  const [format, setFormat] = useState("all");
  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState("popularity");
  const [visible, setVisible] = useState(STEP);

  const regions = useMemo(() => {
    const set = new Set(workItems.map((item) => item.region).filter(Boolean));
    return Array.from(set);
  }, []);

  const years = useMemo(() => {
    const set = new Set(workItems.map((item) => item.year).filter(Boolean));
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, []);

  const filtered = useMemo(() => {
    let list = workItems.filter((item) => {
      if (format !== "all" && item.format !== format) return false;
      if (region !== "all" && item.region !== region) return false;
      if (year !== "all" && item.year !== year) return false;
      return true;
    });
    if (sort === "rating") {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sort === "date") {
      list = sortByDate(list);
    } else {
      list = sortByPopularity(list);
    }
    return list;
  }, [format, region, year, sort]);

  const visibleItems = filtered.slice(0, visible);

  const resetFilters = () => {
    setFormat("all");
    setRegion("all");
    setYear("all");
    setSort("popularity");
    setVisible(STEP);
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品推荐" }]} />

        <div className="mt-4">
          <PageHeader
            title="作品推荐"
            description="这里收录了值得一看的动画与漫画作品。你可以按类型、地区与年份筛选，也可以按热度、更新日期或评分排序，找到最合口味的那一部。"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <span className="flex items-center gap-1 text-sm font-medium text-foreground-700">
            <i className="ri-filter-3-line" />
            筛选
          </span>
          <FilterSelect
            label="类型"
            value={format}
            options={[
              { value: "all", label: "全部类型" },
              { value: "动画", label: "动画" },
              { value: "漫画", label: "漫画" },
            ]}
            onChange={(value) => {
              setFormat(value);
              setVisible(STEP);
            }}
          />
          <FilterSelect
            label="地区"
            value={region}
            options={[{ value: "all", label: "全部地区" }, ...regions.map((r) => ({ value: r, label: r }))]}
            onChange={(value) => {
              setRegion(value);
              setVisible(STEP);
            }}
          />
          <FilterSelect
            label="年份"
            value={year}
            options={[{ value: "all", label: "全部年份" }, ...years.map((y) => ({ value: y, label: y }))]}
            onChange={(value) => {
              setYear(value);
              setVisible(STEP);
            }}
          />
          <FilterSelect
            label="排序"
            value={sort}
            options={[
              { value: "popularity", label: "按热度" },
              { value: "date", label: "按更新日期" },
              { value: "rating", label: "按评分" },
            ]}
            onChange={(value) => {
              setSort(value);
              setVisible(STEP);
            }}
          />
          <span className="ml-auto text-sm text-foreground-500">
            共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
          </span>
        </div>

        <div className="mt-6">
          {visibleItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {visibleItems.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-movie-line"
              title="没有符合条件的作品"
              description="换个类型、地区或年份试试，说不定会有惊喜。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}
        </div>

        {visible < filtered.length ? (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((prev) => prev + STEP)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              加载更多
              <i className="ri-arrow-down-line" />
            </button>
          </div>
        ) : null}
      </div>
    </SiteLayout>
  );
}